frappe.provide('ftcore');

/**
 * Money arithmetic that must never produce a negative.
 *
 * The browser twin of `ftcore/utils/amounts.py`. The two MUST stay identical: a portal or a desk form
 * that recomputes totals client-side is one more implementation of the same calculation, and the
 * whole point of this file is that there is one definition of "floored at zero" rather than a
 * `Math.max(0, ...)` per subtraction.
 *
 * Loaded on Desk via `ftcore.bundle.js` and on web portals via `ct_website.bundle.js`, so both halves
 * of a supplier-facing flow get the same function.
 */

/**
 * Coerce a value to a number the way frappe's Python `flt` does — which `parseFloat` does not.
 *
 * Two divergences that mattered, both confirmed against `flt` on ftcore !168:
 *
 *   `flt("1,234.56")` is 1234.56 — frappe strips digit separators (`data.py:925-926`) — where
 *   `parseFloat` stops at the comma and returns **1**. A formatted amount read back out of a field
 *   looks exactly like that, so the browser was quietly computing with a number 1233.56 too small.
 *
 *   `flt("12abc")` is 0 where `parseFloat` returns 12. `Number()` gives NaN for trailing garbage,
 *   which is what lets the guard below reject it.
 *
 * `null`, `undefined`, `""`, `"abc"` and `NaN` all read as 0 on both sides, as they did before.
 *
 * @param {*} value - Any amount: number, numeric string, null or undefined.
 * @returns {number} The number, or 0 when it is unusable.
 */
function to_number(value) {
    if (typeof value === "string") value = value.replace(/,/g, "");
    const amount = Number(value);
    return isNaN(amount) ? 0 : amount;
}

/**
 * Coerce an amount to a number that is never negative.
 *
 * Replaces a negative with 0 rather than rounding or truncating toward it — replacement is the only
 * one of the three that is exactly "this cannot be negative", which is the property callers rely on.
 *
 * Use it on the RESULT of a subtraction, not on its inputs:
 *
 *     row.total_cost = ftcore.floor_at_zero(row.subtotal - row.discount);
 *
 * Non-numeric input reads as 0, matching the Python `flt` the server floors with: a total that
 * cannot be computed is not a negative total. `null`, `undefined`, `""` and `NaN` all land there.
 *
 * @param {*} value - Any amount: number, numeric string, null or undefined.
 * @returns {number} The amount, or 0 when it is negative or unusable.
 */
ftcore.floor_at_zero = function (value) {
    return Math.max(0, to_number(value));
};

/**
 * Decimal places money is stored and displayed at. The twin of `CURRENCY_PRECISION` in
 * `ftcore/utils/amounts.py` — see that module for why this is a constant and not read from meta.
 */
ftcore.CURRENCY_PRECISION = 2;

/**
 * Round an amount to currency precision.
 *
 * The browser twin of `ftcore.utils.amounts.round_money`. Use it on every money value the page
 * DISPLAYS or submits, at the same points in the calculation the server rounds — a browser that
 * totals to 201.47 while the server stores 201.46875 is the disagreement this pair exists to stop.
 *
 * Apply it to derived money, not to raw per-unit inputs: a rate of 0.125 is a real price, and
 * rounding it to 0.13 changes what the line costs rather than tidying it. Round LAST, after the
 * flooring and capping, and feed the rounded value into whatever is computed from it.
 *
 * Delegates to frappe's global `flt`, which implements the same rounding methods as the Python
 * `flt`, off the same System Settings value — so the two agree on the half-cent cases too. It is
 * loaded on Desk by `desk.bundle.js` and on portals by `frappe-web.bundle.js`, both of which pull
 * in `number_format.js`. The `toFixed` fallback is for a context that has neither; it rounds half
 * away from zero rather than to even, so it can differ from the server on an exact half-cent.
 *
 * @param {*} value - Any amount: number, numeric string, null or undefined.
 * @param {number} [precision] - Decimal places. Defaults to `ftcore.CURRENCY_PRECISION`.
 * @returns {number} The rounded amount, or 0 when the value is unusable.
 */
ftcore.round_money = function (value, precision) {
    const amount = to_number(value);
    const dp = precision === undefined || precision === null ? ftcore.CURRENCY_PRECISION : precision;
    return typeof flt === "function" ? flt(amount, dp) : Number(amount.toFixed(dp));
};
